import { useEffect, useState } from 'react';
import { useOrg } from '@/features/team/OrgProvider';
import { usePeriod } from '@/features/period/PeriodProvider';
import { formatCents } from '@/features/catalog/catalog';
import { fetchOrganizationDebt, type DeudaDeLaOrganizacion } from '@/features/closing/closing';
import { listAllParticipants, type Participant } from '@/features/works/works';
import { fetchDashboard, type DashboardSummary } from './dashboard';
import { RepartoDelMes } from './RepartoPage';

/**
 * Quién facturó y quién ganó en el mes, y de qué trabajo sale cada número.
 *
 * ## Por qué vive acá y no en el Resumen
 *
 * Estuvo en el Resumen del mes, y la misma tabla estaba también en Liquidación:
 * los mismos importes, dos veces, escritos igual a propósito para que no se
 * contradijeran. Resolvimos que dijeran lo mismo en vez de resolver por qué
 * estaban dos veces, y el precio lo pagaba quien entraba: tres pantallas que
 * hablaban de lo mismo y ninguna que dijera cuál era para qué.
 *
 * Ahora cada pantalla contesta una pregunta, y ésta es una sola:
 *
 *   * Resumen del mes → ¿cómo viene el mes?
 *   * Facturar        → ¿qué le cobro a cada cliente?
 *   * **Liquidación** → **¿cuánto le toca a cada uno?**
 *
 * Esto es la tercera, mientras el mes sigue abierto. Al cerrarlo, Liquidación
 * muestra en su lugar los números CONGELADOS del cierre, que es lo que vale a
 * partir de ahí.
 *
 * ## Lo de abajo es de dónde sale
 *
 * El detalle trabajo por trabajo va debajo, y por el mismo motivo: cuando un
 * número no coincide con la planilla, lo primero que se pregunta es de qué
 * visita salió. En el Resumen quedaba lejos de la tabla que lo explicaba.
 */
export function GananciaDelMes({ onVerTrabajo }: { onVerTrabajo: (workId: string) => void }) {
  const { activeOrg } = useOrg();
  const orgId = activeOrg?.organization_id;
  const { year, month } = usePeriod();

  const [data, setData] = useState<DashboardSummary | null>(null);
  /**
   * Lo que la organización le debe a cada uno.
   *
   * Existe por una sola razón: **que este saldo sea el mismo que el del
   * cierre**. Hay clientes que no pagan contra factura y llevan el efectivo a
   * la veterinaria; esa plata entró a la organización y no es ganancia suya. El
   * cierre la reconoce, así que acá también.
   */
  const [deuda, setDeuda] = useState<DeudaDeLaOrganizacion[]>([]);
  const [gente, setGente] = useState<Participant[]>([]);

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const d = await fetchDashboard(orgId, year, month);
        if (!cancelado) setData(d);
      } catch {
        // En silencio: esto es una sección de Liquidación, y lo que la pantalla
        // viene a hacer —cerrar el mes— no depende de que esta tabla cargue.
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, year, month]);

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const [dd, pp] = await Promise.all([
          fetchOrganizationDebt(orgId, year, month),
          listAllParticipants(orgId),
        ]);
        if (cancelado) return;
        setDeuda(dd);
        setGente(pp);
      } catch {
        // Sin esto el saldo queda crudo, y es mejor que inventarlo.
        if (!cancelado) setDeuda([]);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, year, month]);

  /** Lo que la organización le debe a este participante en el período. */
  const deudaDe = (pid: string) => deuda.find((d) => d.participant_id === pid)?.amount_cents ?? 0;

  if (!data) return null;

  // Una organización de una sola persona no tiene saldos entre participantes:
  // la tabla repetiría la producción del mes con otro nombre.
  if (!data.is_admin || data.org_kind !== 'team') return null;

  const sinPrecio = data.production?.pending_count ?? 0;

  // `display: contents` para no agregar una caja: esto se apila con las
  // secciones de la pantalla que lo contiene, no adentro de otra más.
  return (
    <div className="contents" data-testid="ganancia">
      {/* El servidor devuelve `org_result: null` mientras el mes tenga algún
          trabajo sin valorizar. Antes eso dejaba la pantalla muda, y quedarse
          sin explicación es peor que la falta: se entra a ver el reparto, no
          hay nada, y no se sabe si es que no hay o si algo falló. */}
      {data.org_result === null ? (
        <p className="superficie p-4 text-sm text-[var(--aviso)]" data-testid="ganancia-sin-precio">
          {sinPrecio > 0
            ? `Todavía no se puede repartir el mes: hay ${sinPrecio} trabajo${
                sinPrecio === 1 ? '' : 's'
              } sin precio de novillo. Cargá el precio y el reparto aparece solo.`
            : 'Todavía no hay reparto para este mes.'}
        </p>
      ) : (
        <>
          <section className="flex flex-col superficie" data-testid="org-result">
            <h2 className="border-b border-[var(--linea)] px-4 py-2 text-xs font-medium uppercase opacity-60">
              Quién facturó y quién ganó
            </h2>

            <ul className="divide-y divide-[var(--linea)] px-4">
              {data.org_result.map((r) => {
                const gano = r.earned_honoraria_cents + r.earned_mobility_cents;
                const saldo = r.balance_cents + deudaDe(r.participant_id);
                return (
                  <li
                    key={r.participant_id}
                    className="flex flex-col gap-1 py-3"
                    data-testid="org-result-row"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="min-w-0 font-semibold">
                        {r.display_name}
                        {/* Por el TIPO, no por el nombre: la cuenta técnica se
                            puede llamar «Caja» o como quieran. Acá va a parar
                            lo que no tiene facturador asignado, y sin esta
                            aclaración se lee como un socio más. */}
                        {gente.find((g) => g.id === r.participant_id)?.kind === 'org_account' && (
                          <span
                            className="ml-2 rounded-full bg-[var(--superficie-suave)] px-2 py-0.5 text-xs font-normal opacity-80"
                            title="No es una persona. Acá va lo que no tiene facturador asignado, para que las cuentas cierren en cero."
                            data-testid="org-result-cuenta-tecnica"
                          >
                            cuenta de la organización
                          </span>
                        )}
                      </span>
                      <span
                        className="shrink-0 text-lg font-semibold tabular-nums"
                        data-testid="org-result-gano"
                      >
                        {formatCents(gano)}
                      </span>
                    </div>

                    <div className="flex justify-between text-sm text-[var(--tinta-suave)]">
                      <span title="Total facturado a nombre de esta persona en el mes, cobrado o no.">
                        Facturó
                      </span>
                      <span className="tabular-nums" data-testid="org-result-facturo">
                        {formatCents(r.billed_cents)}
                      </span>
                    </div>

                    {r.earned_mobility_cents !== 0 && (
                      <div className="flex justify-between text-sm text-[var(--tinta-suave)]">
                        <span>De lo que ganó, movilidad</span>
                        <span className="tabular-nums">{formatCents(r.earned_mobility_cents)}</span>
                      </div>
                    )}

                    {r.adjustments_cents !== 0 && (
                      <div className="flex justify-between text-sm text-[var(--tinta-suave)]">
                        <span>Ajustes</span>
                        <span className="tabular-nums">{formatCents(r.adjustments_cents)}</span>
                      </div>
                    )}

                    {deudaDe(r.participant_id) > 0 && (
                      <div className="flex justify-between text-sm text-[var(--tinta-suave)]">
                        <span>La organización le debe</span>
                        <span className="tabular-nums">
                          {formatCents(deudaDe(r.participant_id))}
                        </span>
                      </div>
                    )}

                    {/* El saldo en castellano. «-$1.120.125» obliga a recordar
                        de qué lado está el menos: si es plata que falta cobrar
                        o plata que hay que poner. */}
                    <p
                      className={`mt-1 text-sm font-medium ${
                        saldo === 0
                          ? 'opacity-60'
                          : saldo > 0
                            ? 'text-[var(--exito)]'
                            : 'text-[var(--peligro)]'
                      }`}
                      data-testid="org-result-saldo"
                    >
                      {saldo === 0
                        ? 'Está a mano'
                        : saldo > 0
                          ? `Le tienen que transferir ${formatCents(saldo)}`
                          : `Tiene que transferir ${formatCents(-saldo)}`}
                    </p>
                  </li>
                );
              })}
            </ul>

            {/* El total, para contrastar contra la planilla de un vistazo. */}
            <div className="flex items-baseline justify-between gap-3 border-t border-[var(--linea)] px-4 py-3 text-sm">
              <span className="opacity-70">Total del mes</span>
              <span className="tabular-nums" data-testid="org-result-total">
                Facturó{' '}
                <strong>
                  {formatCents(data.org_result.reduce((a, r) => a + r.billed_cents, 0))}
                </strong>{' '}
                · ganó{' '}
                <strong>
                  {formatCents(
                    data.org_result.reduce(
                      (a, r) => a + r.earned_honoraria_cents + r.earned_mobility_cents,
                      0,
                    ),
                  )}
                </strong>
              </span>
            </div>

            <p className="px-4 pb-3 text-xs text-[var(--tinta-tenue)]">
              Los saldos del período suman cero: lo que uno cobró de más, otro lo cobró de menos.
              Mientras el mes esté abierto, estos números son de ahora y pueden cambiar; al cerrarlo
              quedan congelados.
            </p>
          </section>

          <RepartoDelMes onVerTrabajo={onVerTrabajo} />
        </>
      )}
    </div>
  );
}
